import { Controller } from "@hotwired/stimulus";
import * as pdfjsLib from "pdfjs-dist";

export default class extends Controller {
  static targets = ["canvas", "overlay", "container", "pageInfo"];

  static values = {
    url: String,
    fields: Array,
  };

  connect() {
    this.currentPage = 1;
    this.totalPages = 0;
    this.pdfDoc = null;

    pdfjsLib.GlobalWorkerOptions.workerSrc = "/pdf.worker.mjs";

    if (this.urlValue) {
      this.loadDocument();
    }
  }

  async loadDocument() {
    try {
      this.pdfDoc = await pdfjsLib.getDocument(this.urlValue).promise;
      this.totalPages = this.pdfDoc.numPages;
      this.currentPage = 1;

      await this.renderPage(this.currentPage);
      this.updatePageInfo();
    } catch (error) {
      console.error("Failed to load PDF:", error);
    }
  }

  async renderPage(pageNum) {
    const page = await this.pdfDoc.getPage(pageNum);
    const viewport = page.getViewport({ scale: 1.0 });

    const containerWidth = this.containerTarget.offsetWidth;
    const scale = containerWidth / viewport.width;
    const scaledViewport = page.getViewport({ scale });

    this.canvasTarget.width = scaledViewport.width;
    this.canvasTarget.height = scaledViewport.height;

    const ctx = this.canvasTarget.getContext("2d");
    await page.render({ canvasContext: ctx, viewport: scaledViewport }).promise;

    this.overlayTarget.style.width = `${scaledViewport.width}px`;
    this.overlayTarget.style.height = `${scaledViewport.height}px`;

    this.renderSignHereIndicators();
  }

  previousPage() {
    if (this.currentPage > 1) {
      this.currentPage--;
      this.renderPage(this.currentPage);
      this.updatePageInfo();
    }
  }

  nextPage() {
    if (this.currentPage < this.totalPages) {
      this.currentPage++;
      this.renderPage(this.currentPage);
      this.updatePageInfo();
    }
  }

  updatePageInfo() {
    if (this.hasPageInfoTarget) {
      const pageFields = (this.fieldsValue || []).filter(
        (f) => !f.page || f.page === this.currentPage,
      );
      const sigCount = pageFields.filter((f) => f.type !== "date").length;
      const parts = [];
      if (sigCount > 0)
        parts.push(
          `${sigCount} signature${sigCount > 1 ? "s" : ""} needed`,
        );
      const badge =
        parts.length > 0
          ? ` <span style="color: #dc3545; font-weight: 600;">(${parts.join(", ")})</span>`
          : "";
      this.pageInfoTarget.innerHTML = `${this.currentPage} of ${this.totalPages}${badge}`;
    }
  }

  renderSignHereIndicators() {
    this.overlayTarget
      .querySelectorAll(".sign-here-indicator")
      .forEach((el) => el.remove());

    const fields = this.fieldsValue || [];
    const overlayWidth = this.overlayTarget.offsetWidth;
    const overlayHeight = this.overlayTarget.offsetHeight;

    const signatureFields = fields.filter((f) => f.type !== "date");
    const dateFields = fields.filter((f) => f.type === "date");

    fields.forEach((field, index) => {
      if (field.page && field.page !== this.currentPage) return;

      const isDate = field.type === "date";
      const color = isDate ? "#0d6efd" : "#dc3545";
      const bgColor = isDate
        ? "rgba(13, 110, 253, 0.08)"
        : "rgba(220, 53, 69, 0.08)";

      const el = document.createElement("div");
      el.className = "sign-here-indicator";
      el.style.cssText = `
        position: absolute;
        left: ${(field.x / 100) * overlayWidth}px;
        top: ${(field.y / 100) * overlayHeight}px;
        width: ${(field.width / 100) * overlayWidth}px;
        height: ${(field.height / 100) * overlayHeight}px;
        border: 2px solid ${color};
        background: ${bgColor};
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: ${isDate ? "9px" : "11px"};
        font-weight: 600;
        color: ${color};
        pointer-events: none;
        z-index: 10;
        animation: pulse-border 2s ease-in-out infinite;
      `;

      if (isDate) {
        el.innerHTML = `<i class="bi bi-calendar-date" style="margin-right: 3px;"></i> Date`;
      } else {
        el.innerHTML = `<i class="bi bi-pen" style="margin-right: 4px;"></i> Sign Here${signatureFields.length > 1 ? ` (${signatureFields.indexOf(field) + 1})` : ""}`;
      }

      this.overlayTarget.appendChild(el);
    });
  }
}
